import React, { useState } from 'react';
import { InfoIcon } from 'lucide-react';
import { Tooltip } from '../../Tooltip';
interface CollectionDetailsStepProps {
  formData: {
    name: string;
    displaySequence: number;
    isPopular: boolean;
    type: string;
    description: string;
  };
  updateFormData: (data: Partial<CollectionDetailsStepProps['formData']>) => void;
  onNext: () => void;
  onCancel: () => void;
}
export const CollectionDetailsStep: React.FC<CollectionDetailsStepProps> = ({
  formData,
  updateFormData,
  onNext,
  onCancel
}) => {
  const [nameError, setNameError] = useState('');
  const handleSubmit = e => {
    e.preventDefault();
    // Validation
    if (!formData.name.trim()) {
      setNameError('Collection name is required');
      return;
    }
    onNext();
  };
  return <form onSubmit={handleSubmit} className="p-6" data-id="element-230">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-231">
        <div className="col-span-2" data-id="element-232">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-233">
            Collection Name*
          </label>
          <input type="text" id="name" value={formData.name} onChange={e => {
          updateFormData({
            name: e.target.value
          });
          setNameError('');
        }} placeholder="e.g., Student Phones" className={`mt-1 block w-full border ${nameError ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500`} data-id="element-234" />
          {nameError && <p className="mt-1 text-sm text-red-600" data-id="element-235">{nameError}</p>}
        </div>
        <div data-id="element-236">
          <label htmlFor="sequence" className="block text-sm font-medium text-gray-700" data-id="element-237">
            Display Sequence
          </label>
          <input type="number" id="sequence" min="1" value={formData.displaySequence} onChange={e => updateFormData({
          displaySequence: parseInt(e.target.value)
        })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-238" />
          <p className="mt-1 text-xs text-gray-500" data-id="element-239">
            Lower numbers appear first
          </p>
        </div>
        <div data-id="element-240">
          <label className="block text-sm font-medium text-gray-700" data-id="element-241">
            Popular Status
          </label>
          <div className="mt-2 flex items-center" data-id="element-242">
            <button type="button" role="switch" aria-checked={formData.isPopular} onClick={() => updateFormData({
            isPopular: !formData.isPopular
          })} className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${formData.isPopular ? 'bg-green-500' : 'bg-gray-200'}`} data-id="element-243">
              <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200 ${formData.isPopular ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-244" />
            </button>
            <span className="ml-3 text-sm text-gray-500" data-id="element-245">
              {formData.isPopular ? 'Featured in popular collections' : 'Not featured'}
            </span>
          </div>
        </div>
        <div className="col-span-2" data-id="element-246">
          <div className="flex items-center mb-2" data-id="element-247">
            <label className="block text-sm font-medium text-gray-700" data-id="element-248">
              Collection Type
            </label>
            <Tooltip content="SPU (Standard Product Unit) represents a product line. SKU (Stock Keeping Unit) represents specific variants of a product." data-id="element-249">
              <InfoIcon className="h-4 w-4 ml-1 text-gray-400" data-id="element-250" />
            </Tooltip>
          </div>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-251">
            <div className={`border ${formData.type === 'spu' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'} rounded-md p-3 cursor-pointer hover:bg-blue-50`} onClick={() => updateFormData({
            type: 'spu'
          })} data-id="element-252">
              <div className="flex items-center" data-id="element-253">
                <input type="radio" id="spu-based" name="collection-type" checked={formData.type === 'spu'} onChange={() => updateFormData({
                type: 'spu'
              })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" data-id="element-254" />
                <label htmlFor="spu-based" className="ml-2 text-sm font-medium text-gray-900" data-id="element-255">
                  SPU Based
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-256">
                Add entire product lines with all their variants
              </p>
            </div>
            <div className={`border ${formData.type === 'sku' ? 'border-green-500 bg-green-50' : 'border-gray-200'} rounded-md p-3 cursor-pointer hover:bg-green-50`} onClick={() => updateFormData({
            type: 'sku'
          })} data-id="element-257">
              <div className="flex items-center" data-id="element-258">
                <input type="radio" id="sku-specific" name="collection-type" checked={formData.type === 'sku'} onChange={() => updateFormData({
                type: 'sku'
              })} className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300" data-id="element-259" />
                <label htmlFor="sku-specific" className="ml-2 text-sm font-medium text-gray-900" data-id="element-260">
                  SKU Specific
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-261">
                Add individual product variants only
              </p>
            </div>
            <div className={`border ${formData.type === 'mixed' ? 'border-purple-500 bg-purple-50' : 'border-gray-200'} rounded-md p-3 cursor-pointer hover:bg-purple-50`} onClick={() => updateFormData({
            type: 'mixed'
          })} data-id="element-262">
              <div className="flex items-center" data-id="element-263">
                <input type="radio" id="mixed" name="collection-type" checked={formData.type === 'mixed'} onChange={() => updateFormData({
                type: 'mixed'
              })} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300" data-id="element-264" />
                <label htmlFor="mixed" className="ml-2 text-sm font-medium text-gray-900" data-id="element-265">
                  Mixed
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-266">
                Add both SPUs and specific SKUs
              </p>
            </div>
          </div>
        </div>
        <div className="col-span-2" data-id="element-267">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-268">
            Description (Optional)
          </label>
          <textarea id="description" rows={3} value={formData.description} onChange={e => updateFormData({
          description: e.target.value
        })} placeholder="Describe this collection..." className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-269" />
        </div>
      </div>
      <div className="mt-8 flex justify-end space-x-3" data-id="element-270">
        <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-271">
          Cancel
        </button>
        <button type="submit" disabled={!formData.name.trim()} className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${formData.name.trim() ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-400 cursor-not-allowed'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`} data-id="element-272">
          Next: Add Products
        </button>
      </div>
    </form>;
};